import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { OcrService } from "../../services/ocr/OcrService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { translationService } from "../../services/translation/TranslationService";
import { labelsColors, RectType } from "../../models/Rect";
import { secondsToOcrTimestamp } from "../inspection/playbackSync";
import { formatDetectionTime } from "../../services/ocr/InspectionInfoStorageModel";

const t = (key: string) => translationService.translate(key);

type View = "main" | "detectionDistance";

// The regions each view draws. Detection and distance are labelled on the same frame (they share
// one timestamp), so they are shown together, as they were drawn.
const VIEW_TYPES: Record<View, RectType[]> = {
  main: ["main"],
  detectionDistance: ["detections", "distance"],
};

interface LabelSettingsViewProps {
  ocrService: OcrService;
  /** The file whose video a frame is grabbed from when no screenshot is held. */
  videoId?: number;
}

/**
 * The text regions this run was labelled with, read-only: the main-information area, and the
 * detection and distance areas, each drawn on the frame it was set on.
 *
 * What OCR read is only as good as where it was told to look, so a header or a distance that
 * came out wrong is usually explained by a region here. Seeing them used to mean reopening the
 * labeling dialog -- an editor, from which an accidental OK re-labels the file.
 *
 * Coordinates are in the frame's own pixels (ImageCanvas draws at the image's natural size), so
 * the overlay is an SVG with that viewBox laid over the image: it scales with it at any width.
 */
const LabelSettingsView: React.FC<LabelSettingsViewProps> = observer(({ ocrService, videoId }) => {
  const [view, setView] = useState<View>("main");
  const types = VIEW_TYPES[view];
  // The frame belongs to the first region of the view; the others were drawn on the same one.
  const dialog = ocrService.dialogs[types[0]];
  const hasAnyRegion = types.some((type) => !!ocrService.dialogs[type].coordinates);

  // The frame to draw on: the screenshot the labeling session left, else one grabbed at the
  // labelled time. Kept per view so switching back does not seek the video again.
  const [frames, setFrames] = useState<Partial<Record<View, string>>>({});
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  const frame = dialog.screenshot || frames[view];

  useEffect(() => {
    if (!hasAnyRegion || dialog.screenshot || frames[view] || videoId === undefined) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const dataUrl = await (videoPlayerService as any).captureScreenshotAt(
          videoId,
          dialog.timestamp ?? 0
        );
        if (!cancelled && dataUrl) setFrames((prev) => ({ ...prev, [view]: dataUrl }));
      } catch (error) {
        console.error("Label frame capture failed:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, hasAnyRegion, dialog.screenshot, dialog.timestamp, videoId]);

  // A new frame has a new natural size; measure it again before drawing.
  useEffect(() => setSize(null), [frame]);

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={view}
        onChange={(_e, next) => next && setView(next)}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="main">{t("ocrService.labelSettings.main")}</ToggleButton>
        <ToggleButton value="detectionDistance">
          {t("ocrService.labelSettings.detectionDistance")}
        </ToggleButton>
      </ToggleButtonGroup>

      {!hasAnyRegion ? (
        <Alert severity="info">{t("ocrService.labelSettings.none")}</Alert>
      ) : (
        <>
          {/* Legend and the frame's time. Colour is never the only cue: each entry is named. */}
          <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: "wrap", alignItems: "center" }}>
            {types.map((type) => (
              <Stack key={type} direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    border: 2,
                    borderColor: labelsColors[type],
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="body2">
                  {t(`ocrService.dialogContent.${type}.coords`)}
                  {!ocrService.dialogs[type].coordinates && ` — ${t("ocrService.labelSettings.notSet")}`}
                </Typography>
              </Stack>
            ))}
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t("ocrService.labelSettings.frameAt")}{" "}
              {formatDetectionTime(secondsToOcrTimestamp(dialog.timestamp ?? 0))}
            </Typography>
          </Stack>

          {frame ? (
            <Box sx={{ position: "relative", lineHeight: 0 }}>
              <Box
                component="img"
                src={frame}
                alt=""
                onLoad={(e: React.SyntheticEvent<HTMLImageElement>) =>
                  setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
                }
                sx={{ width: "100%", height: "auto", display: "block", borderRadius: 1 }}
              />
              {size && (
                <svg
                  viewBox={`0 0 ${size.w} ${size.h}`}
                  preserveAspectRatio="none"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                >
                  {types.map((type) => {
                    const r = ocrService.dialogs[type].coordinates;
                    if (!r) return null;
                    return (
                      <rect
                        key={type}
                        x={r.x}
                        y={r.y}
                        width={r.w}
                        height={r.h}
                        fill="none"
                        stroke={labelsColors[type]}
                        strokeWidth={Math.max(2, size.w / 400)}
                      />
                    );
                  })}
                </svg>
              )}
            </Box>
          ) : loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Alert severity="warning">{t("ocrService.labelSettings.noFrame")}</Alert>
          )}
        </>
      )}
    </Box>
  );
});

export default LabelSettingsView;
